// Captain mood machine (spec-captain §Runtime), adapted from chess-td's
// Game.js getKingMood(): sticky moods with per-mood timeouts, the highest
// MOOD_PRIORITY wins among simultaneously-active moods, and the callback
// fires on change only. Game events call the note* hooks; the per-frame
// evaluate() call adds the contextual moods (wary/bored/idle). Death is
// terminal — once noteGameOver() lands, every later evaluate() stays death.
// Emissions pass the bark gate: chatter waits out BARK_COOLDOWN_MS between
// spoken lines while BARK_PREEMPT beats cut straight through; gated lines
// are dropped, not queued (a stale bark is worse than a skipped one).

import type { CaptainMood } from './dictionary.js';

/** Highest priority wins when multiple moods are simultaneously active. */
export const MOOD_PRIORITY: Readonly<Record<CaptainMood, number>> = {
  hit: 100,
  death: 95,
  wary: 80,
  flawless: 60,
  streak: 55,
  boss: 52,
  wavestart: 50,
  bored: 20,
  idle: 0
};

/**
 * Minimum gap between spoken barks. Sustained contact otherwise rotates
 * mood lines faster than the typewriter can render them — barks at ~2 Hz
 * were flagged in review as unreadable noise.
 */
export const BARK_COOLDOWN_MS = 8000;

/**
 * Moods that preempt the bark cooldown and always speak immediately: the
 * death/final line, a big hit, a flawless clear, and the boss beat.
 * Everything else (streaks, wave starts, wary/bored/idle chatter) waits
 * out BARK_COOLDOWN_MS.
 */
export const BARK_PREEMPT: Readonly<Partial<Record<CaptainMood, true>>> = {
  death: true,
  hit: true,
  flawless: true,
  boss: true
};

/** Live context for the contextual moods, supplied by the caller each frame. */
export interface MoodContext {
  /** Player HP fraction in [0, 1]. */
  hp01: number;
  /** Enemies within the captain's comfort radius. */
  enemiesNear: number;
  /** ms since the last kill / hit / wave event. */
  quietMs: number;
}

export interface MoodState {
  mood: CaptainMood;
  intensity: number;
  since: number;
}

const HIT_MS = 1800;
const WAVESTART_MS = 1500;
const FLAWLESS_MS = 3000;
const BOSS_MS = 2500;
const STREAK_WINDOW_MS = 2000;
const STREAK_KILLS = 5;
const BORED_AFTER_MS = 6000;
const BORED_RAMP_MS = 10000;
const WARY_HP = 0.5;
const WARY_ENEMIES = 3;

export class CaptainMoods {
  private _state: MoodState;
  private _recentKills: number[] = [];
  private _hitUntil = 0;
  private _wavestartUntil = 0;
  private _flawlessUntil = 0;
  private _bossUntil = 0;
  private _damageThisWave = 0;
  private _dead = false;
  /** Wall-clock of the last emitted bark; starts cold so the first speaks. */
  private _lastBarkAt = Number.NEGATIVE_INFINITY;

  /**
   * `onMood` fires only when the picked mood changes. `now` is injectable
   * for tests; the hooks use it, evaluate() takes its timestamp explicitly.
   */
  constructor(
    private readonly onMood: (mood: CaptainMood, intensity: number) => void,
    private readonly now: () => number = () => performance.now()
  ) {
    this._state = { mood: 'idle', intensity: 0, since: now() };
  }

  /** Player took a hit — highest-priority mood, sticks for 1.8 s. */
  noteHit(): void {
    if (this._dead) return;
    this._hitUntil = this.now() + HIT_MS;
  }

  /** Damage accumulator for the flawless calc (reset on noteWaveStart). */
  noteDamage(dmg: number): void {
    if (this._dead) return;
    this._damageThisWave += Math.max(0, dmg);
  }

  noteWaveStart(): void {
    if (this._dead) return;
    this._wavestartUntil = this.now() + WAVESTART_MS;
    this._damageThisWave = 0;
  }

  /**
   * `flawless` is the caller's verdict; the machine keeps its own damage
   * accumulator. The captain only celebrates when both agree the wave was
   * clean — a missed noteDamage can't buy a celebration, and neither can a
   * caller that forgot to check.
   */
  noteWaveCleared(flawless: boolean): void {
    if (this._dead) return;
    if (flawless && this._damageThisWave === 0) {
      this._flawlessUntil = this.now() + FLAWLESS_MS;
    }
  }

  /** ≥5 kills inside a 2 s rolling window → streak while the window holds. */
  noteKill(): void {
    if (this._dead) return;
    const t = this.now();
    this._recentKills.push(t);
    this._pruneKills(t);
  }

  noteBossSpawn(): void {
    if (this._dead) return;
    this._bossUntil = this.now() + BOSS_MS;
  }

  /** Terminal: switches to death immediately and never leaves it. */
  noteGameOver(): void {
    if (this._dead) return;
    this._dead = true;
    this._transition('death', 1, this.now());
  }

  get current(): CaptainMood {
    return this._state.mood;
  }

  get dead(): boolean {
    return this._dead;
  }

  /**
   * Collect every active mood, pick the highest-priority one, emit on
   * change. Call once per frame; O(1).
   */
  evaluate(nowMs: number, ctx: MoodContext): MoodState {
    if (this._dead) {
      this._transition('death', 1, nowMs);
      return this._state;
    }
    this._pruneKills(nowMs);

    const active: { mood: CaptainMood; intensity: number }[] = [];
    if (nowMs < this._hitUntil) {
      active.push({ mood: 'hit', intensity: Math.min(1, (this._hitUntil - nowMs) / HIT_MS) });
    }
    if (nowMs < this._flawlessUntil) {
      active.push({ mood: 'flawless', intensity: Math.min(1, (this._flawlessUntil - nowMs) / FLAWLESS_MS) });
    }
    if (this._recentKills.length >= STREAK_KILLS) {
      active.push({ mood: 'streak', intensity: Math.min(1, this._recentKills.length / 10) });
    }
    if (nowMs < this._bossUntil) {
      active.push({ mood: 'boss', intensity: Math.min(1, (this._bossUntil - nowMs) / BOSS_MS) });
    }
    if (nowMs < this._wavestartUntil) {
      active.push({ mood: 'wavestart', intensity: Math.min(1, (this._wavestartUntil - nowMs) / WAVESTART_MS) });
    }
    if (ctx.hp01 < WARY_HP || ctx.enemiesNear > WARY_ENEMIES) {
      const hpPart = 1 - ctx.hp01;
      const proxPart = Math.min(1, ctx.enemiesNear / 8);
      active.push({ mood: 'wary', intensity: Math.max(hpPart, proxPart) });
    }
    if (ctx.quietMs >= BORED_AFTER_MS && ctx.enemiesNear <= 2) {
      active.push({ mood: 'bored', intensity: Math.min(1, (ctx.quietMs - BORED_AFTER_MS) / BORED_RAMP_MS) });
    }
    active.push({ mood: 'idle', intensity: 0 });

    active.sort((a, b) => MOOD_PRIORITY[b.mood] - MOOD_PRIORITY[a.mood]);
    const picked = active[0]!;
    this._transition(picked.mood, picked.intensity, nowMs);
    return this._state;
  }

  /** Clear all state for the next run (the next run hires a NEW captain). */
  reset(): void {
    this._recentKills.length = 0;
    this._hitUntil = 0;
    this._wavestartUntil = 0;
    this._flawlessUntil = 0;
    this._bossUntil = 0;
    this._damageThisWave = 0;
    this._dead = false;
    this._lastBarkAt = Number.NEGATIVE_INFINITY;
    this._state = { mood: 'idle', intensity: 0, since: this.now() };
  }

  private _pruneKills(t: number): void {
    while (this._recentKills.length > 0 && t - this._recentKills[0]! > STREAK_WINDOW_MS) {
      this._recentKills.shift();
    }
  }

  private _transition(mood: CaptainMood, intensity: number, t: number): void {
    if (mood === this._state.mood) {
      this._state.intensity = intensity;
      return;
    }
    this._state = { mood, intensity, since: t };
    // Bark gate: the machine state always moves, but the speech callback is
    // throttled — chatter inside the cooldown window is dropped, never
    // queued. Preempting beats speak and also restart the chatter clock.
    if (!BARK_PREEMPT[mood] && t - this._lastBarkAt < BARK_COOLDOWN_MS) return;
    this._lastBarkAt = t;
    this.onMood(mood, intensity);
  }
}
